import { Dispatch, JSX, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { flatten, isObject } from 'underscore';

import Checkbox from '../checkbox/checkbox';
import { Session } from '@/types/session.types';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import useClickAwayListener from '@/utils/use-click-away-listener';

import styles from './session-select.module.css';
import Pill from '@ui/pill/pill';

enum Validation {
  normal = 'normal',
  ok = 'ok',
  warn = 'warn',
  error = 'error',
  flash = 'flash',
}

interface Props {
  onChange: (sessionIds: number[]) => void;
  sessions: Session[];
  label?: string;
  placeholder?: string;
  selectedSessionIds?: number[];
  errorMessage?: string;
  valid?: Validation;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

interface TagProps {
  session: Session;
  remove: (tag: number) => void;
}

function Tag(props: TagProps) {
  const { session, remove } = props;
  return (
    <Pill
      backgroundColor="var(--gray-1000)"
      borderColor="var(--gray-700)"
      borderWidth="2"
      padding="0 12px"
      className={`S2 ${styles.tag}`}
    >
      <div className="flex fd-row jc-flex-start ai-center">
        <span>{session.title.base} </span>
        {/* Do not use a button element here or we'll get console errors because this element is rendered inside another button */}
        <div
          tabIndex={0}
          className={`square-20 flex fd-row jc-center ai-center margin-l-4 ${styles['remove-button']}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              remove(session.session);
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            remove(session.session);
          }}
        >
          <Icon name={ICONS.CLOSE} size={12} />
        </div>
      </div>
    </Pill>
  );
}

interface SessionsByTrack {
  track: string;
  sessions: Session[];
}

export default function SessionSelect(props: Props): JSX.Element {
  const { onChange, sessions, label, placeholder, selectedSessionIds = [], errorMessage, valid, open, setOpen } = props;

  const sessionSelectorRef = useRef<HTMLButtonElement | null>(null);
  const [width, setWidth] = useState(100);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useClickAwayListener({
    ref: containerRef,
    open,
    onPointerDownOutside: () => setOpen(false),
    onEscape: () => setOpen(false),
    onTabAway: () => setOpen(false),
  });

  const sessionsByTag: SessionsByTrack[] = useMemo(() => {
    // pull all tracks from all sessions, flatten them into one array,
    let allTracks = Array.from(new Set(flatten(sessions.map((session: Session) => session.tracks))).values()).sort();
    allTracks = [...allTracks, 'Misc'];

    const sortedSessions = allTracks.map((track: string | null) => ({
      track: track ?? '',
      sessions: sessions.filter((session: Session) => {
        if (track === 'Misc') {
          return !session.tracks || session.tracks.length === 0;
        }
        return session.tracks?.includes(track ?? '');
      }),
    }));

    return sortedSessions.filter((value) => value.sessions.length > 0);
  }, [sessions]);

  const selectedSessionArr: Session[] = useMemo(() => {
    return sessions.filter((session: Session) => selectedSessionIds.includes(session.session));
  }, [selectedSessionIds, sessions]);

  function removeSession(session: number) {
    onChange(selectedSessionIds.filter((_session: number) => _session !== session));
  }

  function addSession(session: number) {
    const sessionsSet = new Set(selectedSessionIds);
    sessionsSet.add(session);
    onChange(Array.from(sessionsSet.values()));
  }

  function toggleSession(session: string | number, on: boolean) {
    if (on) {
      addSession(session as number);
    } else {
      removeSession(session as number);
    }
  }

  function toggleTrack(track: string | number, on: boolean) {
    // if entire track has been deselected, toggle off all tracks in that session
    if (!on) {
      // Determine which sessions to remove
      const sessionsToRemove = sessionsByTag.find((sess) => sess.track === track)?.sessions.map((s) => s.session) || [];
      // Filter the current selected IDs to remove the sessions from this track
      onChange(selectedSessionIds.filter((id) => !sessionsToRemove.includes(id)));
    } else {
      // if entire track has been selected, toggle on all sessions in that track
      if (track === 'Misc') {
        // Sessions that don't belong to a track
        const miscTrack = sessionsByTag.find((session) => session.track === 'Misc');
        if (!miscTrack) return;
        const miscSessions = miscTrack.sessions.map((t) => t.session);
        const anyMiscSessionsSelected = selectedSessionIds.some((selectedSession) =>
          miscSessions.includes(selectedSession)
        );

        if (anyMiscSessionsSelected) {
          // Deselect all Misc sessions by filtering them out
          onChange(selectedSessionIds.filter((selectedSession) => !miscSessions.includes(selectedSession)));
        } else {
          // Select all Misc sessions by adding them
          onChange([...selectedSessionIds, ...miscSessions]);
        }
      } else {
        // For other tracks, get all sessions for that track
        const sessionsWithTrack = sessions
          .filter((session: Session) => {
            return session.tracks?.includes(track as string);
          })
          .map((session: Session) => session.session);

        // Create a new set to ensure uniqueness and call onChange with the new array
        const sessionsSet = new Set([...selectedSessionIds, ...sessionsWithTrack]);
        onChange(Array.from(sessionsSet.values()));
      }
    }
  }

  function selectAllSessions(_: string | number, on: boolean) {
    if (on) {
      onChange(sessions.map((session: Session) => session.session));
    } else {
      onChange([]);
    }
  }

  function handleToggleOpen() {
    setOpen((prev) => !prev);
  }

  const handleClick = useCallback(
    (e: unknown) => {
      if (!isObject(e)) return;

      const path = e.path || e.composedPath();
      for (const item of path) {
        if (sessionSelectorRef.current === item) {
          return;
        }
      }
      setOpen(false);
    },
    [setOpen]
  );

  useEffect(() => {
    if (open) {
      window.addEventListener('click', handleClick);
    }

    return () => window.removeEventListener('click', handleClick);
  }, [handleClick, open]);

  useEffect(() => {
    const rect = containerRef.current?.getBoundingClientRect();

    if (rect) {
      setWidth(rect.width ?? 0);
    }
  }, [open]);

  const allPartial = selectedSessionIds.length > 0;

  const allChecked = useMemo(() => {
    return sessions.every((session: Session) => selectedSessionIds.includes(session.session));
  }, [selectedSessionIds, sessions]);

  const someChecked = useMemo(() => {
    return sessionsByTag
      .filter((sessionsByTag: SessionsByTrack) =>
        sessionsByTag.sessions.some((session: Session) => selectedSessionIds.includes(session.session))
      )
      .map((sessionsByTag: SessionsByTrack) => sessionsByTag.track);
  }, [selectedSessionIds, sessionsByTag]);

  const allCheckedForTracks = useMemo(() => {
    return sessionsByTag
      .filter((sessionsByTag: SessionsByTrack) =>
        sessionsByTag.sessions.every((session: Session) => selectedSessionIds.includes(session.session))
      )
      .map((sessionsByTag: SessionsByTrack) => sessionsByTag.track);
  }, [selectedSessionIds, sessionsByTag]);

  return (
    <div ref={containerRef} className={classNames(styles['session-select-container'], valid)}>
      <div>
        <label className="margin-b-5 margin-l-16 P2" htmlFor="template-description" color="var(--gray-400)">
          {label}
        </label>

        <button
          className={classNames(
            styles['session-select-input'],
            'clear-button',
            'height-100-percent',
            'width-100-percent',
            'text-left',
            { error: errorMessage }
          )}
          onClick={handleToggleOpen}
          ref={sessionSelectorRef}
        >
          {selectedSessionArr.length > 0 ? (
            selectedSessionArr.map((session: Session) => (
              <Tag session={session} key={session.session} remove={removeSession} />
            ))
          ) : (
            <>
              {placeholder && (
                <div className={`S2 height-100-percent width-100-percent margin-l-8 ${styles.placeholder}`}>
                  {placeholder}
                </div>
              )}
            </>
          )}
        </button>
      </div>

      <div
        className={classNames(styles['tag-select-dropdown'], { [styles.open]: open })}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`S2 ${styles['session-group-item']}`}>
          <Checkbox
            className={styles.checkbox}
            checked={allChecked}
            onChange={selectAllSessions}
            label={'All sessions'}
            value={'all'}
            partial={allPartial}
          />
        </div>

        {sessionsByTag.map((sessionByTag: SessionsByTrack) => {
          return (
            <div key={sessionByTag.track} className={styles['session-group']}>
              <div className={`S2 ${styles['session-group-item']}`}>
                <Checkbox
                  className={styles.checkbox}
                  checked={allCheckedForTracks.includes(sessionByTag.track)}
                  value={sessionByTag.track}
                  onChange={toggleTrack}
                  partial={someChecked.includes(sessionByTag.track)}
                  label={sessionByTag.track}
                />
              </div>

              {sessionByTag.sessions.map((session: Session) => (
                <div key={session.session} className={`S2 ${styles['session-group-item']} ${styles.inner}`}>
                  <Checkbox
                    className={styles.checkbox}
                    checked={selectedSessionIds.includes(session.session)}
                    onChange={toggleSession}
                    value={session.session}
                    label={session.title.base}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {errorMessage && <label className={styles['error-message']}>{errorMessage}</label>}
    </div>
  );
}
